import { TQuote } from 'src/types/props.types';
import { ReplayEvent, TickEvent, MarkerEvent, UserActionEvent, ReplayExport } from './EventSchema';
import { segmentIngestionPipeline } from './SegmentIngestionPipeline';

// A lightweight, bounded in-memory log of tick/candle events to support rewind/playback.
// This will later be extended with segment persistence (IndexedDB) and compression.

export type TTickSource = 'live' | 'history';

export interface TTickEvent {
    type: 'tick';
    epoch: number; // milliseconds epoch from quote.DT
    price: number; // close price or tick quote
    symbol: string;
    granularity: number; // ms granularity (0 for pure tick)
    source: TTickSource;
    ohlc?: {
        open: number;
        high?: number;
        low?: number;
        close: number;
    };
    raw: TQuote; // original quote object (kept for now; may be trimmed later)
}

const MAX_EVENTS = 500_000; // temporary cap; subject to tuning
const TRIM_CHUNK = 50_000; // remove this many when exceeding cap to amortize cost

class EventLog {
    private _events: ReplayEvent[] = [];
    // Epoch index for quick lookup (may collide if same epoch appears; stores last occurrence)
    private _epochIndex: Map<number, number> = new Map();
    private _sortPending = false;
    private _sortTimer: number | null = null;
    private _batchMode = false;

    // Windowing mode for long replays (1-year+)
    private _windowMode = false;
    private _windowSize = 100_000; // Keep 100K events in memory
    private _windowStartEpoch = 0; // Track window bounds
    private _windowEndEpoch = 0;
    
    // Cache bounds to avoid repeated scans - invalidated when events change
    private _cachedBounds: { first: number | null; last: number | null } | null = null;
    private _cachedBoundsSize: number = -1;


    beginBatchAdd() {
        this._batchMode = true;
        console.log('[EventLog] Batch mode enabled - skipping segment ingestion during batch');
    }

    async endBatchAdd() {
        this._batchMode = false;
        console.log('[EventLog] Batch mode ending, finalizing...');
        const startTime = performance.now();
        
        // Use merge-sort for pre-sorted chunks (much faster than full sort)
        console.log('[EventLog] Sorting', this._events.length, 'events (using merge-sort for chunked data)...');
        await this._mergeSortChunksAsync();
        
        console.log('[EventLog] Rebuilding index for', this._events.length, 'events...');
        await this._rebuildEpochIndexAsync();
        
        const indexTime = performance.now();
        console.log(`[EventLog] Finalized in ${(indexTime - startTime).toFixed(0)}ms`);
    }

    async addHistoricalAsync(quotes: TQuote[], symbol: string): Promise<void> {
        if (quotes.length === 0) return;
        
        const startTime = performance.now();
        console.log(`[EventLog] addHistoricalAsync: adding ${quotes.length} quotes (current size: ${this._events.length}, batchMode: ${this._batchMode})`);
        
        // Historical quotes from segments are typically already sorted
        // Check if we need to sort by comparing first/last with existing bounds
        const needsSort = this._events.length > 0 && quotes.length > 0 && !this._batchMode;
        
        // Batch append for better performance
        const newEvents: ReplayEvent[] = [];
        
        // Deduplicate by (epoch, symbol) using a Set for O(1) lookup
        // Since replay mode uses candles (1-minute minimum granularity), epochs are unique per candle.
        // No need to include price in the key - each epoch corresponds to exactly one candle.
        const seenKeys = new Set<string>();
        if (this._batchMode && this._events.length > 0) {
            // In batch mode, build dedup set from existing events to avoid duplicates across chunks
            // Only check the last 10K events to optimize performance (recent duplicates are most common)
            const checkStart = Math.max(0, this._events.length - 10000);
            for (let i = checkStart; i < this._events.length; i++) {
                const evt = this._events[i];
                if (evt.type === 'tick' && evt.symbol === symbol) {
                    seenKeys.add(`${evt.epoch}-${evt.symbol}`);
                }
                // Yield every 1000 items to prevent blocking
                if ((i - checkStart) % 1000 === 0) {
                    await Promise.resolve();
                }
            }
        }
        
        // Process incoming quotes with yielding
        for (let i = 0; i < quotes.length; i++) {
            const q = quotes[i];
            const epoch = q.DT ? q.DT.getTime() : Date.parse(`${q.Date}Z`);
            const price = typeof q.Close === 'number' ? q.Close : (q.prevClose ?? 0);
            const dedupKey = `${epoch}-${symbol}`;
            
            // Skip if we've already seen this (epoch, symbol) pair (candles have unique epochs)
            if (seenKeys.has(dedupKey)) {
                continue;
            }
            seenKeys.add(dedupKey);
            
            newEvents.push({
                type: 'tick',
                epoch,
                price,
                volume: typeof q.Volume === 'number' ? q.Volume : undefined,
                symbol,
                granularity: 0,
                source: 'history',
                ohlc: q.Open !== undefined ? {
                    open: q.Open,
                    high: q.High,
                    low: q.Low,
                    close: q.Close,
                } : undefined,
                raw: q,
            });
            
            // Yield every 1000 items to prevent blocking
            if (i % 1000 === 0) {
                await Promise.resolve();
            }
        }
        
        if (newEvents.length < quotes.length) {
            console.log(`[EventLog] Deduplicated ${quotes.length - newEvents.length} duplicate events`);
        }
        
        // Single array concat is much faster than many individual pushes
        this._events.push(...newEvents);
        
        const appendTime = performance.now();
        console.log(`[EventLog] Appended ${quotes.length} events in ${(appendTime - startTime).toFixed(0)}ms`);
        
        // Only sort if we have mixed old/new data and not in batch mode
        if (needsSort) {
            // Defer sorting to avoid blocking for large batches
            this._deferredSort();
        } else if (!this._batchMode) {
            // Small batch or empty log: rebuild index (skip if in batch mode)
            this._rebuildEpochIndex();
        }
        
        if (!this._batchMode) {
            this._enforceBounds();
        }
        
        const totalTime = performance.now() - startTime;
        console.log(`[EventLog] addHistoricalAsync completed in ${totalTime.toFixed(0)}ms (total events: ${this._events.length})`);
        
        // Skip segment ingestion during batch mode (we're loading from already-persisted segments)
        // Only ingest for live data or small single additions
        if (!this._batchMode && segmentIngestionPipeline.isRunning) {
            console.log('[EventLog] Ingesting', quotes.length, 'quotes into segment pipeline');
            segmentIngestionPipeline.ingestBatch(quotes, symbol, 0);
        }
    }

    addHistorical(quotes: TQuote[], symbol: string) {
        if (quotes.length === 0) return;
        
        const startTime = performance.now();
        console.log(`[EventLog] addHistorical: adding ${quotes.length} quotes (current size: ${this._events.length}, batchMode: ${this._batchMode})`);
        
        // Historical quotes from segments are typically already sorted
        // Check if we need to sort by comparing first/last with existing bounds
        const needsSort = this._events.length > 0 && quotes.length > 0 && !this._batchMode;
        
        // Batch append for better performance
        const newEvents: ReplayEvent[] = [];
        
        // Deduplicate by (epoch, symbol) using a Set for O(1) lookup
        // Since replay mode uses candles (1-minute minimum granularity), epochs are unique per candle.
        const seenKeys = new Set<string>();
        if (this._batchMode && this._events.length > 0) {
            // In batch mode, build dedup set from existing events to avoid duplicates across chunks
            // Only check the last 10K events to optimize performance (recent duplicates are most common)
            const checkStart = Math.max(0, this._events.length - 10000);
            for (let i = checkStart; i < this._events.length; i++) {
                const evt = this._events[i];
                if (evt.type === 'tick' && evt.symbol === symbol) {
                    seenKeys.add(`${evt.epoch}-${evt.symbol}`);
                }
            }
        }
        
        for (const q of quotes) {
            const epoch = q.DT ? q.DT.getTime() : Date.parse(`${q.Date}Z`);
            const price = typeof q.Close === 'number' ? q.Close : (q.prevClose ?? 0);
            const dedupKey = `${epoch}-${symbol}`;
            
            // Skip if we've already seen this (epoch, symbol) pair (candles have unique epochs)
            if (seenKeys.has(dedupKey)) {
                continue;
            }
            seenKeys.add(dedupKey);
            
            newEvents.push({
                type: 'tick',
                epoch,
                price,
                volume: typeof q.Volume === 'number' ? q.Volume : undefined,
                symbol,
                granularity: 0,
                source: 'history',
                ohlc: q.Open !== undefined ? {
                    open: q.Open,
                    high: q.High,
                    low: q.Low,
                    close: q.Close,
                } : undefined,
                raw: q,
            });
        }
        
        if (newEvents.length < quotes.length) {
            console.log(`[EventLog] Deduplicated ${quotes.length - newEvents.length} duplicate events`);
        }
        
        // Single array concat is much faster than many individual pushes
        this._events.push(...newEvents);
        
        const appendTime = performance.now();
        console.log(`[EventLog] Appended ${quotes.length} events in ${(appendTime - startTime).toFixed(0)}ms`);
        
        // Only sort if we have mixed old/new data and not in batch mode
        if (needsSort) {
            // Defer sorting to avoid blocking for large batches
            this._deferredSort();
        } else if (!this._batchMode) {
            // Small batch or empty log: rebuild index (skip if in batch mode)
            this._rebuildEpochIndex();
        }
        
        if (!this._batchMode) {
            this._enforceBounds();
        }
        
        const totalTime = performance.now() - startTime;
        console.log(`[EventLog] addHistorical completed in ${totalTime.toFixed(0)}ms (total events: ${this._events.length})`);
        
        // Skip segment ingestion during batch mode (we're loading from already-persisted segments)
        // Only ingest for live data or small single additions
        if (!this._batchMode && segmentIngestionPipeline.isRunning) {
            console.log('[EventLog] Ingesting', quotes.length, 'quotes into segment pipeline');
            segmentIngestionPipeline.ingestBatch(quotes, symbol, 0);
        }
    }

    addLive(quote: TQuote, symbol: string) {
        this._appendTick(quote, symbol, 'live');
        // Live ticks are appended at end, typically already in order
        // No need to sort for single appends
        this._epochIndex.set(quote.DT ? quote.DT.getTime() : Date.parse(`${quote.Date}Z`), this._events.length - 1);
        this._enforceBounds();
        
        // Also ingest into segment pipeline for long-term storage
        if (segmentIngestionPipeline.isRunning) {
            segmentIngestionPipeline.ingest(quote, symbol, 0);
        }
    }

    addMarker(markerType: string, epoch: number, value?: any, symbol?: string) {
        const event: MarkerEvent = {
            type: 'marker',
            epoch,
            markerType,
            value,
            symbol,
        };
        this._events.push(event);
        this._epochIndex.set(epoch, this._events.length - 1);
        this._enforceBounds();
    }

    addUserAction(action: string, epoch: number, payload?: any) {
        const event: UserActionEvent = {
            type: 'user_action',
            epoch,
            action,
            payload,
        };
        this._events.push(event);
        this._epochIndex.set(epoch, this._events.length - 1);
        this._enforceBounds();
    }

    private _appendTick(quote: TQuote, symbol: string, source: TTickSource) {
        const epoch = quote.DT ? quote.DT.getTime() : Date.parse(`${quote.Date}Z`);
        const price = typeof quote.Close === 'number' ? quote.Close : (quote.prevClose ?? 0);
        const event: TickEvent = {
            type: 'tick',
            epoch,
            price,
            volume: typeof quote.Volume === 'number' ? quote.Volume : undefined,
            symbol,
            granularity: 0,
            source,
            raw: quote,
        };
        this._events.push(event);
        this._epochIndex.set(epoch, this._events.length - 1);
    }
    // Export events as JSON, with optional delta compression
    exportJSON(options?: { compress?: boolean; retentionDays?: number }): ReplayExport {
        let events = this._events;
        // Retention: filter by age if requested
        if (options?.retentionDays) {
            const cutoff = Date.now() - options.retentionDays * 24 * 60 * 60 * 1000;
            events = events.filter(e => e.epoch >= cutoff);
        }
        let compression: 'delta' | 'runlength' | null = null;
        if (options?.compress) {
            // Delta encode prices for ticks
            let lastPrice = 0;
            events = events.map(e => {
                if (e.type === 'tick') {
                    const delta = lastPrice === 0 ? e.price : e.price - lastPrice;
                    lastPrice = e.price;
                    return { ...e, price: delta };
                }
                return e;
            });
            compression = 'delta';
        }
        // TODO: run-length encode volumes if needed
        const first = events.length > 0 ? events[0].epoch : null;
        const last = events.length > 0 ? events[events.length - 1].epoch : null;
        const firstTick = events.find(e => e.type === 'tick') as TickEvent | undefined;
        return {
            version: 1,
            symbol: firstTick?.symbol || '',
            granularity: firstTick?.granularity || 0,
            startEpoch: first || 0,
            endEpoch: last || 0,
            events,
            compression,
        };
    }

    private _enforceBounds() {
        // CRITICAL: Flush any deferred sorts before trimming to ensure chronological order
        this._flushSort();
        
        if (!this._windowMode) {
            // Original behavior: global cap for non-windowed mode
            if (this._events.length > MAX_EVENTS) {
                const removeCount = Math.min(TRIM_CHUNK, this._events.length - MAX_EVENTS);
                console.log('[EventLog] Enforcing bounds: removing', removeCount, 'old events (non-windowed mode)');
                this._events.splice(0, removeCount);
                this._rebuildEpochIndex();
            }
            return;
        }

        // Windowed mode: trim to window size instead of global cap
        if (this._events.length > this._windowSize) {
            const removeCount = this._events.length - this._windowSize;
            const removedEvents = this._events.splice(0, removeCount);
            
            // Log removed time range for diagnostics
            if (removedEvents.length > 0) {
                const removedFirst = removedEvents[0].epoch;
                const removedLast = removedEvents[removedEvents.length - 1].epoch;
                console.log('[EventLog] WINDOWED TRIM: removed', removeCount, 'events from', 
                    new Date(removedFirst), 'to', new Date(removedLast));
            }
            
            // Update window bounds (data is now sorted, so [0] is earliest, [length-1] is latest)
            if (this._events.length > 0) {
                this._windowStartEpoch = this._events[0].epoch;
                this._windowEndEpoch = this._events[this._events.length - 1].epoch;
            }

            console.log('[EventLog] Window now has', this._events.length, 'events from', 
                new Date(this._windowStartEpoch), 'to', new Date(this._windowEndEpoch));
            this._rebuildEpochIndex();
        }
    }

    // Retrieve events between epochs inclusive (assumes chronological order)
    getRange(startEpoch: number, endEpoch: number): TTickEvent[] {
        // Ensure any deferred sorting is applied before range query
        this._flushSort();
        
        if (this._events.length === 0) return [];
        
        // Warn if requesting outside window (windowed mode only)
        if (this._windowMode) {
            if (startEpoch < this._windowStartEpoch || endEpoch < this._windowStartEpoch) {
                console.warn('[EventLog] Requested range is outside current window, may be incomplete', {
                    requested: { start: new Date(startEpoch), end: new Date(endEpoch) },
                    window: { start: new Date(this._windowStartEpoch), end: new Date(this._windowEndEpoch) }
                });
            }
        }
        
        // Filter to only tick events for range query (non-tick events may break epoch ordering)
        const allTicks = this._events.filter((e): e is TTickEvent => e.type === 'tick');
        
        if (allTicks.length === 0) return [];
        
        // Log diagnostics
        const minEpoch = allTicks[0].epoch;
        const maxEpoch = allTicks[allTicks.length - 1].epoch;
        console.log('[EventLog] getRange called: startEpoch=', new Date(startEpoch), 'endEpoch=', new Date(endEpoch), 'total ticks in log=', allTicks.length, 'min=', new Date(minEpoch), 'max=', new Date(maxEpoch));
        
        // Binary search on tick events only
        const startIdx = this._binarySearchStartInTicks(allTicks, startEpoch);
        if (startIdx === -1) {
            console.warn('[EventLog] getRange: no ticks found at or after startEpoch');
            return [];
        }
        
        const endIdx = this._binarySearchEndInTicks(allTicks, endEpoch);
        if (endIdx === -1) {
            console.warn('[EventLog] getRange: no ticks found at or before endEpoch');
            return [];
        }
        
        const result = allTicks.slice(startIdx, endIdx + 1);
        console.log('[EventLog] getRange returning', result.length, 'ticks from', result.length > 0 ? new Date(result[0].epoch) : 'empty', 'to', result.length > 0 ? new Date(result[result.length - 1].epoch) : 'empty');
        return result;
    }
    
    private _binarySearchStartInTicks(ticks: TTickEvent[], targetEpoch: number): number {
        let left = 0;
        let right = ticks.length - 1;
        let result = -1;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            if (ticks[mid].epoch >= targetEpoch) {
                result = mid;
                right = mid - 1;
            } else {
                left = mid + 1;
            }
        }

        return result;
    }

    private _binarySearchEndInTicks(ticks: TTickEvent[], targetEpoch: number): number {
        let left = 0;
        let right = ticks.length - 1;
        let result = -1;

        while (left <= right) {
            const mid = Math.floor((left + right) / 2);
            if (ticks[mid].epoch <= targetEpoch) {
                result = mid;
                left = mid + 1;
            } else {
                right = mid - 1;
            }
        }

        return result;
    }

    latest(): TTickEvent | undefined {
        for (let i = this._events.length - 1; i >= 0; i--) {
            const e = this._events[i];
            if (e.type === 'tick') return e as TTickEvent;
        }
        return undefined;
    }

    // Robust bounds that do not assume ordering - OPTIMIZED with caching
    getBounds(): { first: number | null; last: number | null } {
        // CRITICAL: Flush any deferred sorts before calculating bounds
        this._flushSort();
        
        // Check cache: if size hasn't changed, return cached bounds
        if (this._cachedBoundsSize === this._events.length && this._cachedBounds !== null) {
            return this._cachedBounds;
        }
        
        // If events are empty, return nulls
        if (this._events.length === 0) {
            this._cachedBounds = { first: null, last: null };
            this._cachedBoundsSize = 0;
            return this._cachedBounds;
        }
        
        // Optimization: Events are sorted, so find first/last tick efficiently
        // Search from the beginning for first tick (earliest)
        let minEpoch: number | null = null;
        for (let i = 0; i < this._events.length; i++) {
            if (this._events[i].type === 'tick') {
                minEpoch = this._events[i].epoch;
                break; // Found first tick
            }
        }
        
        // Search from the end for last tick (latest)
        let maxEpoch: number | null = null;
        for (let i = this._events.length - 1; i >= 0; i--) {
            if (this._events[i].type === 'tick') {
                maxEpoch = this._events[i].epoch;
                break; // Found last tick
            }
        }
        
        this._cachedBounds = { first: minEpoch, last: maxEpoch };
        this._cachedBoundsSize = this._events.length;
        return this._cachedBounds;
    }

    size() {
        return this._events.length;
    }

    /**
     * Enable windowing mode for long replays (1-year+).
     * Keeps only specified number of events in memory (rolling window).
     * Older events are automatically trimmed as new ones arrive.
     */
    enableWindowing(windowSize: number = 100_000): void {
        this._windowMode = true;
        this._windowSize = Math.max(10000, windowSize); // Minimum 10K events
        console.log('[EventLog] Windowing enabled, size:', this._windowSize);
        
        // Apply windowing immediately if already over limit
        this._enforceBounds();
    }

    /**
     * Disable windowing mode and return to global cap behavior.
     */
    disableWindowing(): void {
        this._windowMode = false;
        console.log('[EventLog] Windowing disabled');
    }

    /**
     * Check if epoch is within current window.
     * Returns false if windowing enabled and epoch is outside window bounds.
     */
    isInWindow(epoch: number): boolean {
        if (!this._windowMode) return true;
        return epoch >= this._windowStartEpoch && epoch <= this._windowEndEpoch;
    }

    /**
     * Get current window bounds (epoch time in ms).
     */
    getWindowBounds(): { start: number; end: number; size: number; mode: boolean } {
        return {
            start: this._windowStartEpoch,
            end: this._windowEndEpoch,
            size: this._events.length,
            mode: this._windowMode,
        };
    }

    /**
     * Clear all events from the log.
     * Useful for starting a fresh replay session.
     */
    clear() {
        console.log('[EventLog] Clearing all events (had', this._events.length, 'events)');
        this._events = [];
        this._epochIndex.clear();
        this._sortPending = false;
        this._windowStartEpoch = 0;
        this._windowEndEpoch = 0;
        if (this._sortTimer !== null) {
            clearTimeout(this._sortTimer as number);
            this._sortTimer = null;
        }
    }

    // Get all tick events (for EventStream)
    getAll(): TTickEvent[] {
        // Ensure sort is applied before returning all events
        this._flushSort();
        return this._events.filter((e): e is TTickEvent => e.type === 'tick');
    }

    private _deferredSort() {
        if (this._sortPending) return;
        
        this._sortPending = true;
        console.log(`[EventLog] Scheduling deferred sort for ${this._events.length} events`);
        
        // Use setTimeout to defer sort to next tick, avoiding blocking
        if (this._sortTimer !== null) {
            clearTimeout(this._sortTimer);
        }
        
        this._sortTimer = setTimeout(() => {
            const startTime = performance.now();
            console.log(`[EventLog] Starting deferred sort of ${this._events.length} events...`);
            this._optimizedSort();
            const sortTime = performance.now();
            console.log(`[EventLog] Sort completed in ${(sortTime - startTime).toFixed(0)}ms, rebuilding index...`);
            this._rebuildEpochIndex();
            const indexTime = performance.now();
            console.log(`[EventLog] Index rebuilt in ${(indexTime - sortTime).toFixed(0)}ms`);
            this._sortPending = false;
            this._sortTimer = null;
        }, 0) as unknown as number;
    }

    private _flushSort() {
        if (this._sortPending && this._sortTimer !== null) {
            console.log(`[EventLog] Flushing pending sort for ${this._events.length} events...`);
            const startTime = performance.now();
            clearTimeout(this._sortTimer);
            this._optimizedSort();
            const sortTime = performance.now();
            console.log(`[EventLog] Flush sort completed in ${(sortTime - startTime).toFixed(0)}ms`);
            this._rebuildEpochIndex();
            const indexTime = performance.now();
            console.log(`[EventLog] Flush index rebuilt in ${(indexTime - sortTime).toFixed(0)}ms`);
            this._sortPending = false;
            this._sortTimer = null;
        }
    }

    /**
     * NEW: Detect if events are already mostly sorted by checking disorder ratio.
     * For large datasets from segments (which are pre-sorted), this saves time.
     */
    private _isMostlySorted(): boolean {
        if (this._events.length < 2) return true;

        // Sample 100 random pairs to estimate disorder
        const sampleSize = Math.min(100, Math.floor(this._events.length / 10));
        let disorderedPairs = 0;

        for (let i = 0; i < sampleSize; i++) {
            const idx = Math.floor(Math.random() * (this._events.length - 1));
            if (this._events[idx].epoch > this._events[idx + 1].epoch) {
                disorderedPairs++;
            }
        }

        // If <5% of pairs are disordered, consider it mostly sorted
        const disorderRatio = disorderedPairs / sampleSize;
        const isMostlySorted = disorderRatio < 0.05;

        console.log(`[EventLog] Sort check: ${sampleSize} samples, ${disorderedPairs} disordered (${(disorderRatio * 100).toFixed(1)}%), mostly sorted: ${isMostlySorted}`);
        return isMostlySorted;
    }

    /**
     * NEW: Optimized sort for large pre-sorted datasets.
     * For segments that are already sorted internally, we can skip full sort.
     * Fall back to native sort if data is too disordered.
     */
    private _optimizedSort(): void {
        const startTime = performance.now();

        // CRITICAL: Always use native sort to ensure correct ordering
        // The "mostly sorted" check with 100 samples is insufficient for detecting
        // localized regions of disorder in large arrays (50K+ events)
        // Native sort is fast enough (typically <10ms) and guarantees correctness
        console.log('[EventLog] Sorting', this._events.length, 'events with native sort');
        this._sortByEpoch();
        const elapsed = performance.now() - startTime;
        console.log(`[EventLog] Native sort completed in ${elapsed.toFixed(0)}ms for ${this._events.length} events`);
    }

    private _sortByEpoch() {
        this._events.sort((a, b) => a.epoch - b.epoch);
        
        // Verify sort worked correctly
        let unsortedPairs = 0;
        for (let i = 1; i < this._events.length; i++) {
            if (this._events[i].epoch < this._events[i - 1].epoch) {
                unsortedPairs++;
                if (unsortedPairs <= 5) {
                    console.error('[EventLog] ❌ SORT FAILED at index', i, ':',
                        new Date(this._events[i - 1].epoch), '=>', new Date(this._events[i].epoch),
                        'epochs:', this._events[i - 1].epoch, '=>', this._events[i].epoch);
                }
            }
        }
        if (unsortedPairs > 0) {
            console.error('[EventLog] ❌ Array has', unsortedPairs, 'unsorted pairs after sort! Total events:', this._events.length);
        }
    }

    private _rebuildEpochIndex() {
        this._epochIndex.clear();
        for (let i = 0; i < this._events.length; i++) {
            this._epochIndex.set(this._events[i].epoch, i);
        }
    }

    private async _rebuildEpochIndexAsync() {
        this._epochIndex.clear();
        const BATCH_SIZE = 10000;
        
        for (let i = 0; i < this._events.length; i += BATCH_SIZE) {
            const end = Math.min(i + BATCH_SIZE, this._events.length);
            for (let j = i; j < end; j++) {
                this._epochIndex.set(this._events[j].epoch, j);
            }
            
            // Yield to main thread every 10K events
            if (end < this._events.length) {
                console.log(`[EventLog] Indexed ${end}/${this._events.length} events`);
                await Promise.resolve(); // Yield to event loop
            }
        }
    }

    /**
     * Merge-sort optimized for pre-sorted chunks.
     * When data comes from multiple sorted segments, this is much faster than full sort.
     * Yields to main thread every 10K events to prevent blocking.
     */
    private async _mergeSortChunksAsync(): Promise<void> {
        const startTime = performance.now();
        
        // For small arrays, use native sort
        if (this._events.length < 10000) {
            this._sortByEpoch();
            console.log(`[EventLog] Small dataset, used native sort (${this._events.length} events)`);
            return;
        }

        // Detect if data is already mostly sorted (common after chunked loading)
        if (this._isMostlySorted()) {
            console.log('[EventLog] Data already mostly sorted, using native sort');
            this._sortByEpoch();
            const elapsed = performance.now() - startTime;
            console.log(`[EventLog] Native sort completed in ${elapsed.toFixed(0)}ms`);
            return;
        }

        console.log('[EventLog] Data needs sorting, using async merge-sort with yielding');
        
        // Perform merge-sort with yielding every 10K operations
        this._events = await this._mergeSortWithYield(this._events);
        
        const elapsed = performance.now() - startTime;
        console.log(`[EventLog] Merge-sort completed in ${elapsed.toFixed(0)}ms`);
    }

    /**
     * Async merge-sort that yields to main thread periodically.
     * This prevents blocking the UI during large sorts.
     */
    private async _mergeSortWithYield(events: ReplayEvent[]): Promise<ReplayEvent[]> {
        if (events.length <= 1) return events;

        // For small subarrays, use native sort (faster)
        if (events.length < 1000) {
            return events.sort((a, b) => a.epoch - b.epoch);
        }

        const mid = Math.floor(events.length / 2);
        const left = await this._mergeSortWithYield(events.slice(0, mid));
        const right = await this._mergeSortWithYield(events.slice(mid));

        return await this._mergeWithYield(left, right);
    }

    /**
     * Merge two sorted arrays, yielding to main thread every 10K operations.
     */
    private async _mergeWithYield(left: ReplayEvent[], right: ReplayEvent[]): Promise<ReplayEvent[]> {
        const result: ReplayEvent[] = [];
        let leftIdx = 0;
        let rightIdx = 0;
        let opCount = 0;
        const YIELD_INTERVAL = 10000;

        while (leftIdx < left.length && rightIdx < right.length) {
            if (left[leftIdx].epoch <= right[rightIdx].epoch) {
                result.push(left[leftIdx]);
                leftIdx++;
            } else {
                result.push(right[rightIdx]);
                rightIdx++;
            }

            opCount++;
            // Yield to main thread every 10K operations
            if (opCount % YIELD_INTERVAL === 0) {
                await Promise.resolve();
            }
        }

        // Append remaining elements
        while (leftIdx < left.length) {
            result.push(left[leftIdx]);
            leftIdx++;
        }
        while (rightIdx < right.length) {
            result.push(right[rightIdx]);
            rightIdx++;
        }

        return result;
    }
}

export const eventLog = new EventLog();

declare global {
    interface Window {
        __eventLog?: EventLog;
    }
}

// Expose for debugging / inspection (optional; can be gated by env later)
if (typeof window !== 'undefined') {
    window.__eventLog = eventLog as any;
}
